import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isPushSupported, enablePushNotifications, disablePushNotifications, getPushSubscriptionStatus } from "@/lib/pwa";

/**
 * Push bildirim aç/kapa butonu. Cihaz desteklemiyorsa veya izin reddedilmişse
 * kibar bir uyarı gösterir; aksi halde subscription'ı backend'e kaydeder.
 *
 * iOS Safari için: kullanıcı uygulamayı önce "Ana Ekrana Ekle" demek zorunda;
 * sonra standalone modda butona basabilir. (iOS standalone dışında push'a izin vermiyor.)
 */
export function PushNotificationButton({ size = "sm" }) {
  const [status, setStatus] = useState({ supported: false, subscribed: false, permission: "default" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) { setStatus({ supported: false, subscribed: false, permission: "default" }); return; }
    getPushSubscriptionStatus().then(setStatus).catch(() => {});
  }, []);

  const isStandalone = typeof window !== "undefined" && (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true
  );
  const isiOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

  if (!status.supported) {
    return null; // sessizce gizle — tarayıcı desteklemiyor
  }

  const onToggle = async () => {
    setBusy(true);
    try {
      if (status.subscribed) {
        const res = await disablePushNotifications();
        if (res.ok) {
          toast.success("Bildirimler kapatıldı");
          setStatus(await getPushSubscriptionStatus());
        } else {
          toast.error(res.reason || "Kapatılamadı");
        }
      } else {
        if (isiOS && !isStandalone) {
          toast.error("iPhone'da bildirimler için önce 'Paylaş → Ana Ekrana Ekle' yapın, sonra ana ekrandan açın.");
          setBusy(false);
          return;
        }
        const res = await enablePushNotifications();
        if (res.ok) {
          toast.success("Bildirimler aktif — yeni siparişlerde bilgilendirileceksiniz");
          setStatus(await getPushSubscriptionStatus());
        } else {
          toast.error(res.reason || "Açılamadı");
        }
      }
    } catch (e) {
      toast.error(e?.message || "Beklenmeyen hata");
    } finally {
      setBusy(false);
    }
  };

  const dim = size === "lg" ? "text-sm px-3 py-2" : "text-xs px-2.5 py-1.5";
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        data-testid="push-toggle"
        onClick={onToggle}
        disabled={busy}
        className={`inline-flex items-center gap-1.5 border-2 font-bold uppercase tracking-wider transition-colors disabled:opacity-60 ${dim} ${
          status.subscribed
            ? "border-emerald-500 bg-emerald-50 text-emerald-800 hover:border-emerald-700"
            : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-950"
        }`}
        title={status.subscribed ? "Bildirimler açık — kapatmak için tıkla" : "Bildirimleri aç"}
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> :
         status.subscribed ? <Bell size={14} strokeWidth={2.5} /> : <BellOff size={14} strokeWidth={2.5} />}
        <span className="hidden sm:inline">{status.subscribed ? "Bildirim Açık" : "Bildirim Kapalı"}</span>
      </button>
      {status.subscribed && (
        <button
          type="button"
          data-testid="push-test"
          onClick={async () => {
            try {
              const { data } = await api.post("/push/test-detailed");
              if (!data.ok) { toast.error(data.reason || "Test başarısız"); return; }
              const fail = data.results.filter((r) => !r.ok);
              if (fail.length === 0) toast.success(`${data.sent} cihaza test gönderildi — bildirimi bekleyin`);
              else toast.error(`${fail.length}/${data.sent} cihaz: ${fail[0].error || "hata"}`);
            } catch (e) {
              toast.error("Test gönderilemedi");
            }
          }}
          className={`inline-flex items-center gap-1 border-2 border-zinc-300 hover:border-zinc-950 text-zinc-700 hover:text-zinc-950 font-bold uppercase tracking-wider ${dim}`}
          title="Bu cihaza test bildirimi gönder"
        >
          <Send size={12} strokeWidth={2.5} />
          <span className="hidden sm:inline">Test</span>
        </button>
      )}
    </div>
  );
}
